import domPurify from 'dompurify';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { TopStatusBar } from '../components/domain/TopStatusBar';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAccounts } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import {
  useRegenerateWeeklyReview,
  useUpdateWeeklyReviewNotes,
} from '../features/reviews/mutations';
import { useWeeklyReview } from '../features/reviews/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';
import { formatCurrency } from '../lib/format/currency';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function markdownToHtml(value: string): string {
  const paragraphs = value
    .split(/\n{2,}/)
    .map((block) => escapeHtml(block.trim()))
    .filter(Boolean)
    .map((block) => {
      const withStrong = block.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      const withEmphasis = withStrong.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      return `<p>${withEmphasis.replace(/\n/g, '<br>')}</p>`;
    });
  return domPurify.sanitize(paragraphs.join(''));
}

export function WeeklyReview() {
  const { weekStart } = useParams();
  const me = useMe();
  const accounts = useAccounts();
  const activeAccountId = accounts.data?.[0]?.id ?? null;
  const snapshot = useLatestSnapshot(activeAccountId);
  const review = useWeeklyReview(weekStart ?? null);
  const regenerate = useRegenerateWeeklyReview();
  const updateNotes = useUpdateWeeklyReviewNotes();
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setNotes(review.data?.notes ?? '');
  }, [review.data?.notes]);

  const notesHtml = useMemo(() => markdownToHtml(review.data?.notes ?? ''), [review.data?.notes]);

  if (me.isLoading || review.isLoading) {
    return <main className="p-6 text-text-muted">Loading weekly review...</main>;
  }
  if (me.isError || review.isError || !me.data || !review.data || !weekStart) {
    return <main className="p-6 text-danger">Could not load weekly review.</main>;
  }

  const metrics = review.data.metrics;
  const flags = Object.entries(review.data.flagsSummary.countsByKind).sort(
    (left, right) => right[1] - left[1],
  );

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Week of {review.data.weekStart}</h1>
            <p className="mt-1 text-sm text-text-muted">
              {metrics.weekStart} to {metrics.weekEnd}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="secondary">
              <Link to="/reviews/weekly">All reviews</Link>
            </Button>
            <Button
              type="button"
              onClick={() => regenerate.mutate(weekStart)}
              disabled={regenerate.isPending}
            >
              Regenerate
            </Button>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-4">
          <Metric
            label="Realised P&L"
            value={formatCurrency(metrics.headline.realisedPnl, me.data.baseCurrency)}
          />
          <Metric
            label="Unrealised P&L"
            value={formatCurrency(metrics.headline.unrealisedPnl, me.data.baseCurrency)}
          />
          <Metric
            label="Delta NAV"
            value={
              metrics.headline.deltaNav
                ? formatCurrency(metrics.headline.deltaNav, me.data.baseCurrency)
                : '-'
            }
          />
          <Metric label="Drawdown" value={`${(metrics.headline.drawdown * 100).toFixed(1)}%`} />
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <Card
            header={
              <div>
                <h2 className="text-lg font-semibold">Headline metrics</h2>
                <p className="text-sm text-text-muted">Closed lots: {metrics.closedLotsCount}</p>
              </div>
            }
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Metric
                label="Win rate"
                value={metrics.winRate === null ? '-' : `${(metrics.winRate * 100).toFixed(0)}%`}
              />
              <Metric
                label="Expectancy"
                value={metrics.expectancyR === null ? '-' : `${metrics.expectancyR.toFixed(2)}R`}
              />
              <Metric
                label="Profit factor"
                value={metrics.profitFactor === null ? '-' : metrics.profitFactor.toFixed(2)}
              />
            </div>
            <div className="mt-5 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.rMultipleDistribution}>
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} width={32} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            header={
              <div>
                <h2 className="text-lg font-semibold">Rules adherence</h2>
                <p className="text-sm text-text-muted">
                  {metrics.adherence.rate === null
                    ? 'No trades this week'
                    : `${(metrics.adherence.rate * 100).toFixed(0)}% planned pre-trade`}
                </p>
              </div>
            }
          >
            <div className="space-y-3">
              <Metric
                label="Pre-trade entries"
                value={`${metrics.adherence.preTradeEntries}/${metrics.adherence.totalTrades}`}
              />
              <Metric
                label="Single biggest issue"
                value={
                  metrics.singleBiggestIssue
                    ? `${metrics.singleBiggestIssue.ruleKey} (${metrics.singleBiggestIssue.count})`
                    : '-'
                }
              />
            </div>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card header={<h2 className="text-lg font-semibold">Top winners</h2>}>
            <PnlList
              rows={metrics.topWinners}
              currency={me.data.baseCurrency}
              empty="No winners closed."
            />
          </Card>
          <Card header={<h2 className="text-lg font-semibold">Top losers</h2>}>
            <PnlList
              rows={metrics.topLosers}
              currency={me.data.baseCurrency}
              empty="No losers closed."
            />
          </Card>
        </div>

        <Card header={<h2 className="text-lg font-semibold">Behavioural flag counts</h2>}>
          {flags.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {flags.map(([kind, count]) => (
                <Metric key={kind} label={kind.replaceAll('_', ' ')} value={String(count)} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-muted">No behavioural flags this week.</p>
          )}
        </Card>

        <Card header={<h2 className="text-lg font-semibold">Lessons</h2>}>
          {metrics.lessons.length ? (
            <ul className="space-y-2 text-sm">
              {metrics.lessons.map((lesson) => (
                <li key={lesson} className="rounded border border-border bg-bg-muted p-3">
                  {lesson}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-text-muted">No post-trade lessons logged this week.</p>
          )}
        </Card>

        <Card header={<h2 className="text-lg font-semibold">User notes</h2>}>
          <div
            className="prose prose-sm max-w-none text-text"
            dangerouslySetInnerHTML={{ __html: notesHtml }}
          />
          <textarea
            className="mt-4 min-h-40 w-full rounded border border-border bg-bg p-3 text-sm"
            aria-label="Weekly review notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              disabled={updateNotes.isPending}
              onClick={() => updateNotes.mutate({ weekStart, body: { notes } })}
            >
              Save notes
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-bg-muted p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 break-words font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function PnlList({
  currency,
  empty,
  rows,
}: {
  currency: string;
  empty: string;
  rows: Array<{ symbol: string; pnl: string }>;
}) {
  if (rows.length === 0) return <p className="text-sm text-text-muted">{empty}</p>;
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={`${row.symbol}-${row.pnl}`}
          className="flex items-center justify-between rounded border border-border bg-bg-muted p-3"
        >
          <span className="font-semibold">{row.symbol}</span>
          <span className="tabular-nums">{formatCurrency(row.pnl, currency)}</span>
        </div>
      ))}
    </div>
  );
}
